import { TranslateService } from '@ngx-translate/core';
import { BasePlugin, Meta, UppyFile } from '@uppy/core';
import imageCompression from 'browser-image-compression';

import { FileUploadOptionsModel } from '../../models/interfaces/file-upload.model';

type ImageCompressorOptions = FileUploadOptionsModel['imageCompressor'] & {
    id?: string;
};

interface CompressionResult {
    compressedFile: File;
    wasCompressed: boolean;
    compressionRatio: string;
    originalSize: number;
}

export default class ImageCompressor extends BasePlugin<ImageCompressorOptions, Meta, Record<string, never>> {
    static readonly VERSION = '1.0.0';

    // Constants
    private static readonly BYTES_PER_KB = 1024;
    private static readonly BYTES_PER_MB = ImageCompressor.BYTES_PER_KB * ImageCompressor.BYTES_PER_KB;
    private static readonly PERCENTAGE_MULTIPLIER = 100;
    private static readonly SIZE_DECIMAL_PLACES = 2;
    private static readonly COMPRESSION_RATIO_MULTIPLIER = 10;

    private static readonly NOTIFICATION_TIMEOUTS = {
        SUCCESS: 3000,
        INFO: 2000,
        NOTICE: 1000,
        ERROR: 4000
    } as const;

    private static readonly PLUGIN_NAME = 'ImageCompressor';

    public readonly id: string;
    public readonly type: string;
    public readonly opts: ImageCompressorOptions;
    public translateService: TranslateService | undefined;

    constructor(uppy: any, options: ImageCompressorOptions = {}) {
        super(uppy, options);
        this.type = 'modifier';
        this.id = options.id || ImageCompressor.PLUGIN_NAME;
        this.opts = options;
        this.translateService = options.translateService;

        this.handleFileAdded = this.handleFileAdded.bind(this);
    }

    install(): void {
        this.uppy.on('file-added', this.handleFileAdded);
    }

    uninstall(): void {
        this.uppy.off('file-added', this.handleFileAdded);
    }

    async handleFileAdded(file: UppyFile<Meta, Record<string, never>>): Promise<void> {
        if (!this.isImageFile(file)) {
            return;
        }

        const message = this.translateService?.instant('FileUploadCompressionStart', { filename: file.name }) ?? '';
        this.uppy.info(message, 'info', ImageCompressor.NOTIFICATION_TIMEOUTS.NOTICE);

        try {
            const result = await this.processImageFile(file);
            this.handleCompressionResult(file, result);
        } catch (error) {
            this.handleCompressionError(file, error);
        }
    }

    async compressImage(file: File, options: ImageCompressorOptions): Promise<File> {
        if (options.quality !== undefined) {
            options.maxSizeMB = (file.size * options.quality) / ImageCompressor.BYTES_PER_MB; // Convert bytes to MB
        }
        if (this.shouldCompressFile(file, options.compressTypes)) {
            try {
                return await imageCompression(file, options);
            } catch (error) {
                this.logError('Image compression failed', error);
                throw error;
            }
        }
        return file;
    }

    private isImageFile(file: UppyFile<Meta, Record<string, never>>): boolean {
        return (file?.data as File)?.type && (file?.data as File)?.type.startsWith('image/') ? true : false;
    }

    private async processImageFile(file: UppyFile<Meta, Record<string, never>>): Promise<CompressionResult> {
        const compressedFile = await this.compressImage(file.data as File, this.opts);
        const wasCompressed = compressedFile.size < (file.data as File).size;
        const compressionRatio = this.calculateCompressionRatio((file.data as File).size, compressedFile.size);

        return {
            compressedFile,
            wasCompressed,
            compressionRatio,
            originalSize: (file.data as File).size
        };
    }

    private handleCompressionResult(file: UppyFile<Meta, Record<string, never>>, result: CompressionResult): void {
        if (result.wasCompressed) {
            this.updateFileWithCompressedVersion(file, result);
            this.showCompressionSuccessMessage((file.data as File).name, result);
        } else {
            this.showAlreadyCompressedMessage((file.data as File).name);
        }
    }

    private handleCompressionError(file: UppyFile<Meta, Record<string, never>>, error: any): void {
        this.logError(`Failed to compress ${file.data.name}`, error);
        this.showCompressionErrorMessage(file.data.name);
    }

    private updateFileWithCompressedVersion(file: UppyFile<Meta, Record<string, never>>, result: CompressionResult): void {
        this.uppy.setFileState(file.id, {
            data: result.compressedFile,
            size: result.compressedFile.size,
            name: file.name || result.compressedFile.name,
            type: result.compressedFile.type
        });
    }

    private calculateCompressionRatio(originalSize: number, compressedSize: number): string {
        const ratio = ((originalSize - compressedSize) / originalSize) * ImageCompressor.PERCENTAGE_MULTIPLIER;
        // Round to the nearest 10% increment (50%, 60%, 70%, etc.)
        const roundedRatio = Math.round(ratio / ImageCompressor.COMPRESSION_RATIO_MULTIPLIER) * ImageCompressor.COMPRESSION_RATIO_MULTIPLIER;
        return roundedRatio.toString();
    }

    private convertBytesToMB(bytes: number): string {
        return (bytes / ImageCompressor.BYTES_PER_MB).toFixed(ImageCompressor.SIZE_DECIMAL_PLACES);
    }

    // Notification and logging methods
    private showCompressionSuccessMessage(filename: string, result: CompressionResult): void {
        const sizeInMB = this.convertBytesToMB(result.compressedFile.size);
        const compression = `${result.compressionRatio}% (${sizeInMB} MB)`;
        const message = this.translateService?.instant('FileUploadCompressionSuccess', { filename, compression }) ?? '';
        this.uppy.info(message, 'info', ImageCompressor.NOTIFICATION_TIMEOUTS.SUCCESS);
    }

    private showAlreadyCompressedMessage(filename: string): void {
        const message = this.translateService?.instant('FileUploadCompressionInfo', { filename }) ?? '';
        this.uppy.info(message, 'info', ImageCompressor.NOTIFICATION_TIMEOUTS.INFO);
    }

    private showCompressionErrorMessage(filename: string): void {
        const message = this.translateService?.instant('FileUploadCompressionError', { filename }) ?? '';
        this.uppy.info(message, 'error', ImageCompressor.NOTIFICATION_TIMEOUTS.ERROR);
    }

    private logError(message: string, error: any): void {
        // eslint-disable-next-line no-console
        console.error(`[${ImageCompressor.PLUGIN_NAME}] ${message}:`, error);
    }

    private shouldCompressFile(file: File, compressTypes?: string[]): boolean {
        if (compressTypes?.includes('image/*')) {
            return true;
        }
        return compressTypes?.includes(file.type) ?? false;
    }
}
